import { Prescription, PrescriptionGroup, PrescriptionHospitalGroup, Patient } from "common/models/types";

/**
 * 개별 처방전 목록을 처방전 그룹 목록으로 변환하는 함수
 * @param prescriptions 개별 처방전 목록
 * @returns 처방전 그룹 목록
 */
export const groupPrescriptions = (prescriptions: Prescription[]): PrescriptionGroup[] => {
  if (!prescriptions || prescriptions.length === 0) {
    return [];
  }

  // 그룹화를 위한 임시 객체
  const groupMap: Record<string, PrescriptionGroup> = {};

  // 처방전을 그룹으로 묶기
  prescriptions.forEach((prescription) => {
    // 그룹화 키 생성
    const groupKey = createGroupKey(prescription);

    // 기존 그룹 확인 또는 새 그룹 생성
    if (!groupMap[groupKey]) {
      groupMap[groupKey] = {
        groupId: prescription.prescriptionGroupId || groupKey,
        prescriptionDate: prescription.prescriptionDate || new Date(),
        hospital: prescription.hospital || "",
        patientId: prescription.patientId,
        prescriptions: [],
        deletedPrescriptionIds: [],
      };
    }

    // 그룹에 처방전 추가
    groupMap[groupKey].prescriptions.push(prescription);
  });

  // 그룹 내 처방전을 시퀀스 번호로 정렬
  Object.values(groupMap).forEach((group) => {
    group.prescriptions.sort((a, b) => {
      const seqA = a.prescriptionSequence || 0;
      const seqB = b.prescriptionSequence || 0;
      return seqA - seqB;
    });
  });

  // 그룹 목록 생성 및 날짜로 정렬
  return Object.values(groupMap).sort((a, b) => {
    return new Date(b.prescriptionDate).getTime() - new Date(a.prescriptionDate).getTime();
  });
};

/**
 * 처방전에 대한 그룹 키를 생성
 * 그룹 ID가 있으면 그룹 ID 사용, 없으면 날짜 및 약품명 조합으로 생성
 */
const createGroupKey = (prescription: Prescription): string => {
  if (prescription.prescriptionGroupId) {
    return prescription.prescriptionGroupId;
  }

  // 처방일이 있으면 처방일 사용, 없으면 현재 날짜 사용
  const dateStr = prescription.prescriptionDate
    ? new Date(prescription.prescriptionDate).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];

  // 처방일 + 임의의 해시값
  return `${dateStr}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 빈 처방전 그룹 생성
 * @param patientId 환자 ID (선택 사항)
 * @returns 빈 처방전 그룹
 */
export const createEmptyPrescriptionGroup = (patientId?: number): PrescriptionGroup => {
  // 그룹 ID 생성: 환자ID-생성타임스탬프 16진수
  const patientIdStr = patientId ? patientId.toString() : "0";
  const timestamp = Date.now().toString(16); // 타임스탬프를 16진수로 변환

  const groupId = `${patientIdStr}-${timestamp}`;

  return {
    groupId,
    patientId,
    prescriptionDate: new Date(),
    prescriptions: [],
    deletedPrescriptionIds: [],
  };
};

/**
 * 처방전 그룹에 빈 처방전 추가
 * @param group 처방전 그룹
 * @param patientId 환자 ID
 * @returns 새 처방전이 추가된 처방전 그룹 (불변성 유지)
 */
export const addEmptyPrescriptionToGroup = (
  group: PrescriptionGroup,
  patientId?: number
): PrescriptionGroup => {
  // 그룹의 처방약 중 가장 높은 sequence 값 찾기
  const maxSequence = group.prescriptions.reduce(
    (max, p) => (p.prescriptionSequence ? Math.max(max, p.prescriptionSequence) : max),
    0
  );

  // 새 처방약 생성
  const newPrescription: Prescription = {
    patientId,
    prescriptionGroupId: group.groupId,
    prescriptionDate: group.prescriptionDate,
    prescriptionSequence: maxSequence + 1,
    medicationName: "",
    dosagePerAdministration: 0,
    dosageUnit: "",
    timesPerDay: 0,
    daysOfAdministration: 0,
    usageInstruction: "",
    insuranceCode: "",
    notes: "",
  };

  // 새 처방전 추가된 그룹 반환 (불변성 유지)
  return {
    ...group,
    prescriptions: [...group.prescriptions, newPrescription],
  };
};

/**
 * 처방전 그룹을 개별 처방전 목록으로 변환하는 함수
 * @param group 처방전 그룹
 * @returns 개별 처방전 목록
 */
export const flattenPrescriptionGroup = (group: PrescriptionGroup): Prescription[] => {
  if (!group || !group.prescriptions) {
    return [];
  }

  // 각 처방전에 그룹 정보 설정
  return group.prescriptions.map((prescription, index) => {
    // drug 객체를 제외한 나머지 속성만 추출
    const { drug, ...prescriptionWithoutDrug } = prescription;

    return {
      ...prescriptionWithoutDrug,
      prescriptionGroupId: group.groupId,
      prescriptionDate: group.prescriptionDate,
      prescriptionSequence: index + 1,
      hospital: group.hospital,
      patientId: group.patientId,
    };
  });
};

/**
 * 환자 데이터에서 처방전(prescription) 목록을 그룹화하여 prescriptionGroups 필드를 생성/업데이트
 * @param patient 환자 데이터
 * @returns prescriptionGroups가 업데이트된 새 환자 객체
 */
export const updatePatientPrescriptionGroups = (patient: Patient): Patient => {
  if (!patient || !patient.prescriptions || patient.prescriptions.length === 0) {
    return {
      ...patient,
      prescriptionGroups: [],
    };
  }

  // 처방전 목록을 그룹화
  const prescriptionGroups = groupPrescriptions(patient.prescriptions);

  // 새 환자 객체 반환
  return {
    ...patient,
    prescriptionGroups,
  };
};

/**
 * 처방전 그룹 목록을 병원별로 그룹화하는 함수
 * @param prescriptionGroups 처방전 그룹 목록 (날짜순으로 정렬되어 있다고 가정)
 * @returns 병원별로 그룹화된 처방전 그룹 목록 (최근 처방받은 병원 순서)
 */
export const groupPrescriptionsByHospital = (
  prescriptionGroups: PrescriptionGroup[]
): PrescriptionHospitalGroup[] => {
  if (!prescriptionGroups || prescriptionGroups.length === 0) {
    return [];
  }

  // 병원별 그룹을 순서대로 저장할 배열
  const hospitalGroups: PrescriptionHospitalGroup[] = [];
  // 병원명으로 그룹을 빠르게 찾기 위한 맵
  const hospitalIndexMap: Record<string, number> = {};

  // 처방전 그룹을 순회하면서 병원별로 묶기 (이미 날짜순으로 정렬되어 있음)
  prescriptionGroups.forEach((group) => {
    const hospital = group.hospital || "병원명 없음";

    // 기존 병원 그룹이 있는지 확인
    if (hospital in hospitalIndexMap) {
      // 기존 병원 그룹에 추가
      const index = hospitalIndexMap[hospital];
      hospitalGroups[index].prescriptionGroups.push(group);
    } else {
      // 새 병원 그룹 생성
      const newGroup: PrescriptionHospitalGroup = {
        hospital,
        prescriptionGroups: [group],
      };
      hospitalGroups.push(newGroup);
      hospitalIndexMap[hospital] = hospitalGroups.length - 1;
    }
  });

  // 처방전 그룹이 이미 날짜순으로 정렬되어 있으므로,
  // 각 병원 그룹 내부의 처방전 그룹도 이미 날짜순으로 정렬되어 있음
  // 따라서 추가 정렬 불필요

  return hospitalGroups;
};

/**
 * 서버로 전송하기 전에 환자 데이터에서 프론트엔드 전용 필드 제거
 * @param patient 환자 데이터
 * @returns 서버 전송용 환자 데이터
 */
export const preparePatientForServer = (patient: Patient): Patient => {
  // prescriptionGroups 필드 제거하여 새 객체 반환
  const { prescriptionGroups, ...patientForServer } = patient;
  return patientForServer;
};

/**
 * 처방전 그룹 변경 후 환자의 prescriptions 배열 업데이트
 * @param patient 환자 데이터
 * @param updatedGroup 업데이트된 처방전 그룹
 * @returns 처방전이 업데이트된 환자 객체
 */
export const updatePatientPrescriptionsFromGroup = (
  patient: Patient,
  updatedGroup: PrescriptionGroup
): Patient => {
  if (!patient) return patient;

  // 기존 처방전 목록 복사
  const prescriptions = [...(patient.prescriptions || [])];

  // 처방전 그룹 ID로 기존 처방전 필터링
  const filteredPrescriptions = prescriptions.filter(
    (p) => p.prescriptionGroupId !== updatedGroup.groupId
  );

  // 업데이트된 처방전 목록
  const updatedPrescriptions = [
    ...filteredPrescriptions,
    ...flattenPrescriptionGroup(updatedGroup),
  ];

  // 업데이트된 환자 객체 반환
  const updatedPatient = {
    ...patient,
    prescriptions: updatedPrescriptions,
  };

  // prescriptionGroups도 업데이트
  return updatePatientPrescriptionGroups(updatedPatient);
};

/**
 * 처방전 그룹 삭제 후 환자의 prescriptions 배열 업데이트
 * @param patient 환자 데이터
 * @param groupId 삭제할 처방전 그룹 ID
 * @returns 처방전이 업데이트된 환자 객체
 */
export const deletePatientPrescriptionGroup = (patient: Patient, groupId: string): Patient => {
  if (!patient) return patient;

  // 해당 그룹 ID 처방전 제외한 처방전 목록 생성
  const updatedPrescriptions = (patient.prescriptions || []).filter(
    (p) => p.prescriptionGroupId !== groupId
  );

  // 업데이트된 환자 객체 반환
  const updatedPatient = {
    ...patient,
    prescriptions: updatedPrescriptions,
  };

  // prescriptionGroups도 업데이트
  return updatePatientPrescriptionGroups(updatedPatient);
};
